import React, { Fragment, useMemo } from "react";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Breadcrumbs } from "components/navigation/Breadcrumbs";
import { LicenceCheckingCreditsStats } from "pages/licence-checking/credits/licenceCheckingCreditStats";
import { LicenceCheckingCreditsPurchase } from "pages/licence-checking/credits/LicenceCheckingCreditsPurchase";
import { LicenceCheckingCreditExpiry } from "pages/licence-checking/credits/credit-expiry";
import { HorizontalLinkGroup } from "components/navigation";
import { Route, Routes, Navigate } from "react-router-dom";
import { useQuery } from "hooks/useQuery/useQuery";
import { useUserHasAccess } from "hooks/useUserHasAccess";
import { CreditCount } from "pages/licence-checking/shared/credit-count/CreditCount";

const manageAction = "LicenceChecking:Manage";
const creditExpiryLocation = "/licence-checking/credits/expiry";

const creditsPageActions = [
    manageAction,
    "LicenceChecking:View",
    "LicenceChecking:GreyFleet",
];

export function LicenceCheckingCredits() {
    const userHasAccess = useUserHasAccess();

    const query = useQuery();
    const fromGreyFleet = query.get("from") === "greyfleet";
    const breadcrumbs = fromGreyFleet
        ? [
              { location: "/home", title: "HOME" },
              {
                  location: "/licence-checking/grey-fleet",
                  title: "GREY FLEET",
              },
              { title: "MANAGE CREDITS" },
          ]
        : [
              { location: "/home", title: "HOME" },
              { location: "/licence-checking", title: "LICENCE CHECKING HUB" },
              { title: "MANAGE CREDITS" },
          ];

    // Appended to every tab route so the grey-fleet origin survives tab clicks and the default redirect.
    const originSuffix = fromGreyFleet ? "?from=greyfleet" : "";

    const routes = useMemo(() => {
        return [
            {
                location: `/licence-checking/credits/usage${originSuffix}`,
                title: "Credit Statistics",
                permission: manageAction,
            },
            {
                location: `/licence-checking/credits/purchase${originSuffix}`,
                title: "Purchase Credits",
                permission: "LicenceChecking:PurchaseCredits",
            },
            {
                location: `${creditExpiryLocation}${originSuffix}`,
                title: "Credit Expiry",
            },
        ]
            .filter(r => !r.permission || userHasAccess(r.permission));
    }, [userHasAccess, originSuffix]);

    // Credit managers land on Credit Statistics, which filtering leaves first; everyone else lands on
    // Credit Expiry, whatever other actions they hold. Both branches already carry the origin suffix.
    const defaultLocation = userHasAccess(manageAction) ? routes[0].location : `${creditExpiryLocation}${originSuffix}`;

    return (
        <UserAccess
            perform={creditsPageActions}
            yes={() => (
                <Fragment>
                    <div className="row">
                        <Breadcrumbs items={breadcrumbs} />
                        <h1>Manage Credits</h1>
                        <div className="button-row button-row-stacked-mobile spacer-bottom">
                            <div className="inline-items inline-items-space-reverse space-large spacer-bottom-large">
                                <CreditCount hideManageButton />
                                <HorizontalLinkGroup
                                    links={routes}
                                    exactRouteMatch={false}
                                ></HorizontalLinkGroup>
                            </div>
                        </div>

                        <Routes>
                            <Route path="usage" element={<LicenceCheckingCreditsStats />} />
                            <Route path="purchase" element={<LicenceCheckingCreditsPurchase />} />
                            <Route path="expiry" element={<LicenceCheckingCreditExpiry />} />
                            <Route path="" element={<Navigate to={defaultLocation} replace />} />
                        </Routes>
                    </div>
                </Fragment>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}
